import './SegmentationContextMenu.scss';
import { Point } from 'leaflet';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { ColumnLayout } from '../../../../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { useMap } from '../../../../../../../useMap';
import { MOUSE_BUTTONS } from '../../../../annotations.interface';
import { BringToFrontContextMenuItemContainer } from '../containers/menuItems/BringToFrontContextMenuItem.container';
import { DeleteSegmentationContextMenuItemContainer } from '../containers/menuItems/DeleteSegmentationContextMenuItem.container';
import { SendToBackContextMenuItemContainer } from '../containers/menuItems/SendToBackContextMenuItem.container';

interface IProps {
  position: Point | undefined;
  segmentationId: string | undefined;
  onMenuClose(): void;
}

const Offset = 2;

export const SegmentationContextMenu = observer((props: IProps): ReactElement => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentHeight, setCurrentHeight] = useState(0);
  const [currentWidth, setCurrentWidth] = useState(0);
  const map = useMap();

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    map.on('zoomstart', handleMenuClosed);

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
      map.off('zoomstart', handleMenuClosed);
    };
  }, []);

  useEffect(() => {
    const height = wrapperRef.current?.clientHeight || 0;
    const width = wrapperRef.current?.clientWidth || 0;
    if (height !== currentHeight || width !== currentWidth) {
      setCurrentHeight(height);
      setCurrentWidth(width);
    }
  }, [currentHeight, currentWidth]);

  const handleClickOutside = (event: MouseEvent): void => {
    const targetElement = event.target as HTMLElement;
    const isRightClick = (event.buttons & MOUSE_BUTTONS.RIGHT) === MOUSE_BUTTONS.RIGHT;
    const isSelfClick = wrapperRef.current?.contains(event.target as Node);
    if (!isSelfClick && (!targetElement.classList.contains('with-context-menu') || !isRightClick)) {
      handleMenuClosed();
    }
  };

  const handleMouseOver = (): void => {
    map.dragging.disable();
  };

  const handleMouseLeave = (): void => {
    map.dragging.enable();
  };

  const handleMenuClosed = (): void => {
    map.dragging.enable();
    props.onMenuClose();
  };

  const calculateCorrectPosition = (position: Point): { x: number; y: number } => {
    let x = position.x + Offset;
    let y = position.y + Offset;

    const mapWrapperElement = document.getElementsByClassName('map-wrapper')[0] as HTMLElement;

    if (currentHeight === undefined) {
      return { x: mapWrapperElement.clientWidth, y: mapWrapperElement.clientHeight };
    }

    if (x + currentWidth > mapWrapperElement.clientWidth) {
      x = x - currentWidth;
    }

    if (y + currentHeight > mapWrapperElement.clientHeight) {
      y = y - currentHeight;
    }

    return { x, y };
  };

  if (!props.position || !props.segmentationId) {
    return <></>;
  }

  const correctPosition = calculateCorrectPosition(props.position);

  return (
    <div
      ref={wrapperRef}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      className='segmentation-context-menu'
      style={{ left: correctPosition.x, top: correctPosition.y }}
    >
      <ColumnLayout size='none'>
        <DeleteSegmentationContextMenuItemContainer
          onMenuClose={handleMenuClosed}
          segmentationId={props.segmentationId}
        />
        <BringToFrontContextMenuItemContainer
          onMenuClose={handleMenuClosed}
          segmentationId={props.segmentationId}
        />
        <SendToBackContextMenuItemContainer
          onMenuClose={handleMenuClosed}
          segmentationId={props.segmentationId}
        />
      </ColumnLayout>
    </div>
  );
});
